import React, { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, CheckCircle2, School, User, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, ORG_TYPES, SERVICE_OPTIONS } from "@/config/content";
import { Reveal, SectionHeading } from "@/components/site/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, name, type = "text", value, onChange, required, error, placeholder, testid }) => (
  <label className="block">
    <span className="text-sm font-medium text-brand-navy">{label}{required && <span className="text-brand-coral"> *</span>}</span>
    <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} required={required}
      data-testid={testid}
      className={`mt-1.5 w-full rounded-xl border bg-white px-4 py-3 text-sm text-brand-navy placeholder:text-brand-ink/40 focus-ring ${error ? "border-brand-coral" : "border-brand-navy/12"}`} />
    {error && <span className="text-xs text-brand-coral mt-1 block">{error}</span>}
  </label>
);

const Sel = ({ label, name, value, onChange, options, required, testid }) => (
  <label className="block">
    <span className="text-sm font-medium text-brand-navy">{label}{required && <span className="text-brand-coral"> *</span>}</span>
    <select name={name} value={value} onChange={onChange} data-testid={testid}
      className="mt-1.5 w-full rounded-xl border border-brand-navy/12 bg-white px-4 py-3 text-sm text-brand-navy focus-ring">
      <option value="">Please select…</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

const initialSchool = { organisation_name: "", name: "", role: "", email: "", phone: "", organisation_type: "", student_count: "", preferred_term: "", suburb: "", message: "", consent: false, honeypot: "" };
const initialFamily = { name: "", email: "", phone: "", service_of_interest: "", preferred_contact: "", message: "", consent: false, honeypot: "" };

export const Contact = () => {
  const [tab, setTab] = useState("school");
  const [school, setSchool] = useState(initialSchool);
  const [family, setFamily] = useState(initialFamily);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onS = (e) => setSchool((p) => ({ ...p, [e.target.name]: e.target.value }));
  const onF = (e) => setFamily((p) => ({ ...p, [e.target.name]: e.target.value }));

  const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const submit = async (e) => {
    e.preventDefault();
    const data = tab === "school" ? school : family;
    const errs = {};
    if (!data.name.trim()) errs.name = "Required";
    if (!emailOk(data.email)) errs.email = "Enter a valid email";
    if (tab === "school" && !data.organisation_name.trim()) errs.organisation_name = "Required";
    if (!data.consent) errs.consent = "Consent is required";
    setErrors(errs);
    if (Object.keys(errs).length) { toast.error("Please check the highlighted fields."); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API}/enquiry`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enquiry_type: tab, ...data }),
      });
      if (!res.ok) throw new Error("Request failed");
      setDone(true);
      // Google Analytics: track successful enquiry submission as a lead/conversion
      if (typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          enquiry_type: tab, // "school" or "family"
          form_name: tab === "school" ? "School enquiry" : "Family enquiry",
        });
      }
      toast.success("Thank you — your enquiry has been sent.");
      setSchool(initialSchool); setFamily(initialFamily);
    } catch (err) {
      toast.error("Something went wrong. Please call or email us directly.");
    } finally { setLoading(false); }
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-white">
      <div className="max-site container-px grid lg:grid-cols-2 gap-14">
        {/* Left: info */}
        <div>
          <SectionHeading overline="Contact" title="Book a program or ask a question" intro="Choose the enquiry type that suits you. We'll be in touch to discuss your needs." />
          <div className="mt-8 space-y-3">
            <a href={`tel:${CONTACT.phoneIntl}`} data-testid="contact-call-link" className="card-hover flex items-center gap-4 bg-brand-bg rounded-2xl p-5 border border-brand-navy/5">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-brand-teal/10 text-brand-teal"><Phone className="w-5 h-5" /></span>
              <div><p className="text-xs text-brand-ink/60">Call us</p><p className="font-bold text-brand-navy">{CONTACT.phone}</p></div>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="card-hover flex items-center gap-4 bg-brand-bg rounded-2xl p-5 border border-brand-navy/5">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-brand-teal/10 text-brand-teal"><Mail className="w-5 h-5" /></span>
              <div>
                <p className="text-xs text-brand-ink/60">Email us</p>
                <p className="font-bold text-brand-navy break-all">{CONTACT.email}</p>
                {CONTACT.email2 && <p className="font-bold text-brand-navy break-all">{CONTACT.email2}</p>}
              </div>
            </a>
            <div className="flex items-center gap-4 bg-brand-bg rounded-2xl p-5 border border-brand-navy/5">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-brand-teal/10 text-brand-teal"><MapPin className="w-5 h-5" /></span>
              <div><p className="text-xs text-brand-ink/60">Visit us</p><p className="font-semibold text-brand-navy text-sm">{CONTACT.address}</p><p className="text-xs text-brand-ink/55 mt-0.5">{CONTACT.hours}</p></div>
            </div>
          </div>
          <div className="mt-5 rounded-2xl overflow-hidden ring-1 ring-black/5 soft-shadow">
            <iframe title="Straight Sight location map" src={CONTACT.mapsEmbed} width="100%" height="240" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>

        {/* Right: form */}
        <div>
          <div className="flex gap-2 p-1.5 bg-brand-bg rounded-full border border-brand-navy/5 w-full sm:w-max">
            <button onClick={() => { setTab("school"); setErrors({}); setDone(false); }} data-testid="tab-school"
              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors focus-ring ${tab === "school" ? "bg-brand-teal text-white" : "text-brand-navy/70"}`}>
              <School className="w-4 h-4" /> School / Childcare
            </button>
            <button onClick={() => { setTab("family"); setErrors({}); setDone(false); }} data-testid="tab-family"
              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors focus-ring ${tab === "family" ? "bg-brand-teal text-white" : "text-brand-navy/70"}`}>
              <User className="w-4 h-4" /> Patient / Family
            </button>
          </div>

          {done ? (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-3xl border border-brand-teal/25 bg-brand-teal/6 p-10 text-center" data-testid="form-success">
              <CheckCircle2 className="w-14 h-14 text-brand-teal mx-auto" />
              <h3 className="mt-4 font-bold text-xl text-brand-navy">Enquiry received</h3>
              <p className="text-brand-ink/75 mt-2">Thank you. The Straight Sight team will be in touch soon.</p>
              <button onClick={() => setDone(false)} className="mt-6 text-brand-teal font-semibold focus-ring">Send another enquiry</button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="enquiry-form" noValidate>
              {/* honeypot */}
              <input type="text" name="honeypot" tabIndex={-1} autoComplete="off" value={tab === "school" ? school.honeypot : family.honeypot} onChange={tab === "school" ? onS : onF} className="hidden" aria-hidden="true" />

              {tab === "school" ? (
                <>
                  <Field label="School or centre name" name="organisation_name" value={school.organisation_name} onChange={onS} required error={errors.organisation_name} testid="f-org-name" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Contact person" name="name" value={school.name} onChange={onS} required error={errors.name} testid="f-contact-name" />
                    <Field label="Position or role" name="role" value={school.role} onChange={onS} testid="f-role" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Email" name="email" type="email" value={school.email} onChange={onS} required error={errors.email} testid="f-email" />
                    <Field label="Phone" name="phone" value={school.phone} onChange={onS} testid="f-phone" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Sel label="Organisation type" name="organisation_type" value={school.organisation_type} onChange={onS} options={ORG_TYPES} testid="f-org-type" />
                    <Field label="Approx. number of students" name="student_count" value={school.student_count} onChange={onS} testid="f-students" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Preferred term or date" name="preferred_term" value={school.preferred_term} onChange={onS} testid="f-term" />
                    <Field label="Suburb" name="suburb" value={school.suburb} onChange={onS} testid="f-suburb" />
                  </div>
                  <label className="block"><span className="text-sm font-medium text-brand-navy">Message</span>
                    <textarea name="message" value={school.message} onChange={onS} rows={3} data-testid="f-message" className="mt-1.5 w-full rounded-xl border border-brand-navy/12 bg-white px-4 py-3 text-sm focus-ring" /></label>
                </>
              ) : (
                <>
                  <Field label="Name" name="name" value={family.name} onChange={onF} required error={errors.name} testid="f-family-name" />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Email" name="email" type="email" value={family.email} onChange={onF} required error={errors.email} testid="f-family-email" />
                    <Field label="Phone" name="phone" value={family.phone} onChange={onF} testid="f-family-phone" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Sel label="Service of interest" name="service_of_interest" value={family.service_of_interest} onChange={onF} options={SERVICE_OPTIONS} testid="f-service" />
                    <Sel label="Preferred contact method" name="preferred_contact" value={family.preferred_contact} onChange={onF} options={["Email", "Phone"]} testid="f-contact-method" />
                  </div>
                  <label className="block"><span className="text-sm font-medium text-brand-navy">Message</span>
                    <textarea name="message" value={family.message} onChange={onF} rows={3} data-testid="f-family-message" className="mt-1.5 w-full rounded-xl border border-brand-navy/12 bg-white px-4 py-3 text-sm focus-ring" /></label>
                  <p className="text-xs text-brand-ink/55">Please do not include detailed medical information in this form.</p>
                </>
              )}

              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" name="consent" checked={tab === "school" ? school.consent : family.consent}
                  onChange={(e) => (tab === "school" ? setSchool : setFamily)((p) => ({ ...p, consent: e.target.checked }))}
                  data-testid="f-consent" className="mt-1 w-4 h-4 accent-brand-teal focus-ring" />
                <span className={`text-sm ${errors.consent ? "text-brand-coral" : "text-brand-ink/75"}`}>I consent to Straight Sight contacting me regarding this enquiry.</span>
              </label>

              <button type="submit" disabled={loading} data-testid="submit-enquiry" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal hover:bg-brand-tealdark disabled:opacity-60 text-white font-semibold py-3.5 transition-colors focus-ring">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Submit enquiry</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
